/**
 * MeetingRoom — the user's live demo meeting page.
 *
 * Layout:
 *   Left  → Dashboard iframe
 *   Right → Audio call controls + bot speaking indicator + countdown timer
 *
 * Reads ?room=…&token=…&url=… from the URL query string,
 * connects to the audio room, and manages the microphone track.
 *
 * Session policy: ONE session at a time. Timer matches backend via /api/config/public sessionLimitSeconds
 * (REACT_APP_SESSION_LIMIT_SECONDS is only the initial fallback before that fetch).
 * The room auto-closes when:
 *   1. The backend broadcasts 'session-ended' via socket, OR
 *   2. The local session countdown reaches 0, OR
 *   3. The bot participant disconnects from the room.
 * In all cases the session-ended screen shows Pain points + Summary from the transcript.
 *
 * If the user refreshes this URL (F5), they are redirected to `/` — recap state is in-memory only
 * and reloading would be confusing or leave a stale token in the address bar.
 *
 * Browser Back/Forward are handled app-wide in `BrowserHistoryLock` (App.js). This page only
 * redirects a full reload to home (recap is in-memory).
 */

import React, { useEffect, useLayoutEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import {
  Room,
  RoomEvent,
  Track,
  createLocalAudioTrack,
  ConnectionState,
  ConnectionError,
  ConnectionErrorReason,
} from 'livekit-client';
import { isLoginPhasePortalLabel } from '../utils/portalScreenshotLabel';
import { normalizePortalScreenshotB64, sameDemoSessionId } from '../utils/portalFrame';
import { isLikelyBlankWhitePngBase64 } from '../utils/portalScreenshotQuality';
import { getApiBaseUrl } from '../utils/apiBase';
import { shouldOpenCalendarFollowupSheet } from '../utils/calendarPitchDetector';
import './LiveKitRoom.css';

const BOT_UI_NAME = (process.env.REACT_APP_BOT_DISPLAY_NAME || 'Hilda').trim() || 'Hilda';
const HILDA_CALENDAR_URL = (process.env.REACT_APP_HILDA_CALENDAR_URL || '').trim();
const HILDA_PRICING_EMAIL = (process.env.REACT_APP_HILDA_PRICING_EMAIL || 'john@withhilda.com').trim();

/**
 * Horizontal / vertical alignment (0–1) for object-position, matching the rendered <img>.
 * Browsers usually normalize `top center` → `50% 0%`.
 */
function parseObjectPositionPercents(img) {
  if (!img || typeof getComputedStyle !== 'function') return { px: 0.5, py: 0.5 };
  const raw = (getComputedStyle(img).objectPosition || '50% 50%').trim();
  const mPct = raw.match(/(-?[\d.]+)%\s+(-?[\d.]+)%/);
  if (mPct) {
    return { px: parseFloat(mPct[1]) / 100, py: parseFloat(mPct[2]) / 100 };
  }
  const mMix = raw.match(/(-?[\d.]+)%\s+(-?[\d.]+)px/i);
  if (mMix) {
    const px = parseFloat(mMix[1]) / 100;
    const yPx = parseFloat(mMix[2]);
    return { px, py: yPx === 0 ? 0 : 0.5 };
  }
  const s = raw.toLowerCase();
  let px = 0.5;
  let py = 0.5;
  if (s.includes('left') && !s.includes('right')) px = 0;
  if (s.includes('right') && !s.includes('left')) px = 1;
  if (s.includes('top') && !s.includes('bottom')) py = 0;
  if (s.includes('bottom') && !s.includes('top')) py = 1;
  return { px, py };
}

/**
 * Playwright sends relX/relY (0–1) in the same coordinate space as the viewport / PNG.
 * The <img> uses object-fit + object-position; offsets must match CSS or the cursor misses clicks.
 */
function mapPortalRelToScreenPoint(panelEl, relX, relY, viewportW, viewportH) {
  if (!panelEl) return { x: 0, y: 0 };
  const img = panelEl.querySelector('.lk-portal-screenshot-img');
  const cr =
    img && typeof img.getBoundingClientRect === 'function'
      ? img.getBoundingClientRect()
      : panelEl.getBoundingClientRect();
  const nw =
    img && img.naturalWidth > 0 ? img.naturalWidth : Math.max(1, Number(viewportW) || 1440);
  const nh =
    img && img.naturalHeight > 0 ? img.naturalHeight : Math.max(1, Number(viewportH) || 900);
  const rx = Number(relX);
  const ry = Number(relY);
  const vx = rx * nw;
  const vy = ry * nh;

  const cw = cr.width;
  const ch = cr.height;
  if (!(cw > 0 && ch > 0)) {
    return { x: cr.left + rx * cw, y: cr.top + ry * ch };
  }

  const room = panelEl.closest('.lk-room');
  const useCover = room && room.classList.contains('lk-room--screenshot-cover');

  const scale = useCover ? Math.max(cw / nw, ch / nh) : Math.min(cw / nw, ch / nh);
  const dispW = nw * scale;
  const dispH = nh * scale;

  const { px, py } = parseObjectPositionPercents(img);
  const offsetX = cr.left + (cw - dispW) * px;
  const offsetY = cr.top + (ch - dispH) * py;

  return {
    x: offsetX + vx * scale,
    y: offsetY + vy * scale,
  };
}

// ── Animated bot cursor overlay ────────────────────────────────────────────
function BotCursorOverlay() {
  const cursorRef   = useRef(null);
  const posRef      = useRef({ x: window.innerWidth / 2, y: window.innerHeight / 2 });
  const targetRef   = useRef({ x: window.innerWidth / 2, y: window.innerHeight / 2 });
  const rafRef      = useRef(null);
  const [visible,   setVisible]   = useState(false);
  const [clicking,  setClicking]  = useState(false);

  useEffect(() => {
    const easeOutCubic = t => 1 - Math.pow(1 - t, 3);

    const animate = () => {
      const dx = targetRef.current.x - posRef.current.x;
      const dy = targetRef.current.y - posRef.current.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist > 0.5) {
        const step = easeOutCubic(Math.min(1, 0.08));
        posRef.current.x += dx * step;
        posRef.current.y += dy * step;
        if (cursorRef.current) {
          cursorRef.current.style.transform = `translate(${posRef.current.x}px, ${posRef.current.y}px)`;
        }
      }
      rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);

    const handleNav = (e) => {
      const { targetRect } = e.detail || {};
      if (!targetRect) return;
      const cx = targetRect.x + targetRect.width  / 2;
      const cy = targetRect.y + targetRect.height / 2;
      targetRef.current = { x: cx, y: cy };
      setVisible(true);
      setClicking(false);
      // Click flash after cursor reaches destination (~600 ms)
      setTimeout(() => setClicking(true),  600);
      setTimeout(() => setClicking(false), 1000);
    };

    const handlePageLoad = () => {
      setTimeout(() => setVisible(false), 1200);
    };

    window.addEventListener('bot-navigate-overlay', handleNav);
    window.addEventListener('portal-page-loaded',   handlePageLoad);
    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('bot-navigate-overlay', handleNav);
      window.removeEventListener('portal-page-loaded',   handlePageLoad);
    };
  }, []);

  if (!visible) return null;
  return (
    <div
      ref={cursorRef}
      className={`lk-bot-cursor${clicking ? ' lk-bot-cursor--click' : ''}`}
      style={{
        position : 'fixed',
        top      : 0,
        left     : 0,
        transform: `translate(${posRef.current.x}px, ${posRef.current.y}px)`,
        zIndex   : 9999,
        pointerEvents: 'none',
      }}
    >
      <div className="lk-bot-cursor-dot" />
      {clicking && <div className="lk-bot-cursor-ripple" />}
    </div>
  );
}

const API_URL = getApiBaseUrl();
const SESSION_FALLBACK_SEC = parseInt(process.env.REACT_APP_SESSION_LIMIT_SECONDS || '900', 10);

export default function MeetingRoom() {
  const [searchParams] = useSearchParams();
  const navigate       = useNavigate();

  // Full reload on /meeting/room → home (recap is in-memory; token in URL is stale).
  useEffect(() => {
    if (typeof performance === 'undefined') return;
    const entries = performance.getEntriesByType && performance.getEntriesByType('navigation');
    const nav = entries && entries[0];
    const legacy = performance.navigation;
    const legacyReload =
      legacy &&
      typeof legacy.type === 'number' &&
      typeof legacy.TYPE_RELOAD === 'number' &&
      legacy.type === legacy.TYPE_RELOAD;
    if ((nav && nav.type === 'reload') || legacyReload) {
      navigate('/', { replace: true });
    }
  }, [navigate]);

  const livekitUrl = searchParams.get('url')   || process.env.REACT_APP_ROOM_URL || '';
  const token      = searchParams.get('token') || '';

  const roomRef       = useRef(null);
  const localTrackRef = useRef(null);
  const botAudioRef   = useRef(null);
  /** False while effect cleanup runs (Strict Mode remount / unmount) — avoids treating intentional disconnect as failure */
  const roomSessionActiveRef = useRef(true);

  const [connectionState, setConnectionState] = useState('idle');
  const [isMuted,         setIsMuted]         = useState(false);
  const [isBotSpeaking,   setIsBotSpeaking]   = useState(false);
  const [error,           setError]           = useState('');
  const [participants,    setParticipants]     = useState([]); // eslint-disable-line no-unused-vars

  // Portal mode — live Playwright screenshots replace the built-in iframe
  const [portalId, setPortalId] = useState(
    () => searchParams.get('portalId') || sessionStorage.getItem('demo_portal_id') || ''
  );
  const [portalUrl, setPortalUrl] = useState(
    () => sessionStorage.getItem('demo_portal_url') || ''
  );
  const [portalName, setPortalName] = useState(
    () => sessionStorage.getItem('demo_portal_name') || 'Portal Demo'
  );

  const [portalScreenshot,    setPortalScreenshot]    = useState('');
  const [portalLabel,         setPortalLabel]         = useState('Opening portal…');
  /** Shown if dashboard frame never arrives (often wrong API host vs browser URL). */
  const [portalStallHint,    setPortalStallHint]    = useState('');
  const screenshotReceivedRef = useRef(false);
  /** After first good frame: force-refresh + reject blank-white PNGs before LiveKit connects */
  const [portalPrimedForLivekit, setPortalPrimedForLivekit] = useState(false);
  /** Bumped each time a new priming pass starts; stale async loops compare and exit (Strict Mode / room change). */
  const portalPrimeSessionRef = useRef(0);

  /** False until /api/config/public has been checked when URL had no portalId */
  const [portalConfigReady, setPortalConfigReady] = useState(() =>
    Boolean(searchParams.get('portalId') || sessionStorage.getItem('demo_portal_id'))
  );

  const roomRouteKey = searchParams.get('room') || '';

  useEffect(() => {
    setPortalPrimedForLivekit(false);
  }, [roomRouteKey]);

  useEffect(() => {
    if (!portalId || !String(portalId).trim()) {
      setPortalPrimedForLivekit(true);
    }
  }, [portalId]);

  const portalFrameCandidateReady = useMemo(() => {
    if (!portalConfigReady) return false;
    if (!portalId || !String(portalId).trim()) return true;
    return (
      portalScreenshot.length > 80 &&
      !String(portalLabel || '').startsWith('Error:')
    );
  }, [portalConfigReady, portalId, portalScreenshot, portalLabel]);

  /**
   * Gates LiveKit connect + session timer. Default: first good screenshot only (fast).
   * Set REACT_APP_BLOCK_LIVEKIT_UNTIL_PORTAL_PRIMED=1 to wait for refresh + blank-check (slower, fewer white flashes).
   */
  const blockLivekitUntilPortalPrimed =
    (process.env.REACT_APP_BLOCK_LIVEKIT_UNTIL_PORTAL_PRIMED || '').trim() === '1';
  const livekitConnectReady = useMemo(() => {
    if (!portalFrameCandidateReady) return false;
    if (!portalId || !String(portalId).trim()) return true;
    if (blockLivekitUntilPortalPrimed) return portalPrimedForLivekit;
    return true;
  }, [portalFrameCandidateReady, portalId, portalPrimedForLivekit, blockLivekitUntilPortalPrimed]);

  const isPrimingDashboardForAudio =
    Boolean(portalId && portalFrameCandidateReady && !portalPrimedForLivekit);

  /** When 1: hide right column (bot status, conversation, controls); portal uses full width; Mute/Leave/timer in header. */
  const fullWidthDashboard =
    (process.env.REACT_APP_LIVEKIT_FULL_WIDTH_DASHBOARD || '1').trim() === '1';
  /** When 1: object-fit cover (fills panel, may crop edges). Default is contain — full capture visible. */
  const screenshotCoverWidth =
    (process.env.REACT_APP_LIVEKIT_SCREENSHOT_COVER || '').trim() === '1';

  // Transcript display
  const [transcript,      setTranscript]      = useState([]);
  const [isAiThinking,    setIsAiThinking]    = useState(false);
  const [typingText,      setTypingText]      = useState(''); // For typewriter effect
  const [isTyping,        setIsTyping]        = useState(false);
  const transcriptEndRef = useRef(null);
  const typingIntervalRef = useRef(null);
  const calendarSheetShownRef = useRef(false);
  const [calendarSheetOpen, setCalendarSheetOpen] = useState(false);

  const tryOpenCalendarFollowupSheet = useCallback((text) => {
    if (calendarSheetShownRef.current || !text) return;
    if (!shouldOpenCalendarFollowupSheet(text)) return;
    calendarSheetShownRef.current = true;
    setCalendarSheetOpen(true);
  }, []);

  // Session countdown — sessionLimitSec synced from backend /api/config/public (matches server auto-stop)
  const [sessionLimitSec, setSessionLimitSec] = useState(SESSION_FALLBACK_SEC);
  const [timeLeft,        setTimeLeft]        = useState(SESSION_FALLBACK_SEC);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/config/public`);
        const j = await r.json().catch(() => ({}));
        if (cancelled) return;
        const sec = typeof j.sessionLimitSeconds === 'number' ? j.sessionLimitSeconds : null;
        if (sec != null && sec >= 60 && sec <= 24 * 60 * 60) {
          setSessionLimitSec(sec);
          setTimeLeft(sec);
        }
      } catch (_) {
        /* keep REACT_APP fallback */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const [sessionOver, setSessionOver] = useState(false);
  const [endReason,   setEndReason]   = useState('');
  /** How the meet ended — drives the session-ended badge (timer vs call). */
  const [sessionEndKind, setSessionEndKind] = useState(null); // 'timer' | 'call-ended' | null
  const [meetingRecap, setMeetingRecap]     = useState(null); // { painPoints: string[], summary: string }
  const [recapLoading, setRecapLoading]     = useState(false);
  const [recapError,   setRecapError]       = useState('');
  const countdownRef = useRef(null);
  const sessionOverRef = useRef(false);
  const transcriptRef  = useRef([]);
  const typingTextRef    = useRef('');

  useLayoutEffect(() => {
    transcriptRef.current = transcript;
    typingTextRef.current = typingText;
  }, [transcript, typingText]);

  // ── Session-ended handler (called by multiple triggers) ───────────────────

  const endSession = useCallback((reason = 'Session ended', endKind = 'call-ended') => {
    if (sessionOverRef.current) return;
    sessionOverRef.current = true;
    // Flush streaming AI text into the transcript so the recap includes the last reply
    setTypingText((prev) => {
      const t = (prev || '').trim();
      if (t) {
        setTranscript((arr) => [...arr, { type: 'ai', text: t, timestamp: new Date() }]);
      }
      return '';
    });
    setIsTyping(false);
    setIsAiThinking(false);
    setCalendarSheetOpen(false);
    setSessionEndKind(endKind);
    setEndReason(reason);
    setSessionOver(true);
    if (roomRef.current) roomRef.current.disconnect().catch(() => {});
  }, []);

  // ── Countdown — after LiveKit is connected AND we have a connect-ready dashboard (first frame, or primed if blocking) ──

  const isConnected = connectionState === ConnectionState.Connected || connectionState === 'connected';
  const demoSessionLive = livekitConnectReady && isConnected;

  // Scan live streaming AI text — socket chunks can be sparse; catches pricing/calendar pivot reliably
  useEffect(() => {
    if (!demoSessionLive || !typingText) return;
    tryOpenCalendarFollowupSheet(typingText);
  }, [typingText, demoSessionLive, tryOpenCalendarFollowupSheet]);

  useEffect(() => {
    if (!demoSessionLive) return undefined;
    setTimeLeft(sessionLimitSec);
    const tick = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(tick);
          endSession(
            sessionLimitSec >= 60
              ? `Your ${Math.round(sessionLimitSec / 60)}-minute session has ended.`
              : 'Your demo session has ended.',
            'timer'
          );
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    countdownRef.current = tick;
    return () => clearInterval(tick);
  }, [demoSessionLive, endSession, sessionLimitSec]);

  const demoSessionLiveRef = useRef(false);
  demoSessionLiveRef.current = demoSessionLive;

  const prevDemoLiveRef = useRef(false);
  useEffect(() => {
    if (demoSessionLive && !prevDemoLiveRef.current) {
      setTranscript([]);
      setTypingText('');
      setIsTyping(false);
      setIsAiThinking(false);
      calendarSheetShownRef.current = false;
      setCalendarSheetOpen(false);
      sessionOverRef.current = false;
      setSessionOver(false);
      setEndReason('');
      setSessionEndKind(null);
      setMeetingRecap(null);
      setRecapLoading(false);
      setRecapError('');
    }
    prevDemoLiveRef.current = demoSessionLive;
  }, [demoSessionLive]);

  // ── After meet ends: request Pain points + Summary from backend ─────────

  useEffect(() => {
    if (!sessionOver) return undefined;

    let cancelled = false;
    setRecapLoading(true);
    setRecapError('');
    setMeetingRecap(null);

    const roomName = searchParams.get('room') || '';
    const sessionId = roomName.replace(/^demo-/, '');

    const fromTranscript = (transcriptRef.current || [])
      .filter((m) => m.type === 'user' || m.type === 'ai')
      .map((m) => ({
        role: m.type === 'user' ? 'user' : 'assistant',
        content: String(m.text || '').trim(),
      }))
      .filter((m) => m.content);

    const tail = String(typingTextRef.current || '').trim();
    const messages =
      tail && (!fromTranscript.length || fromTranscript[fromTranscript.length - 1].content !== tail)
        ? [...fromTranscript, { role: 'assistant', content: tail }]
        : fromTranscript;

    if (messages.length === 0) {
      setMeetingRecap({
        painPoints: [],
        summary: 'No conversation was captured during this meeting.',
      });
      setRecapLoading(false);
      return undefined;
    }

    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/sessions/meeting-recap`, {
          method : 'POST',
          headers: { 'Content-Type': 'application/json' },
          body   : JSON.stringify({ sessionId, messages }),
        });
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (!res.ok || data.success === false) {
          setRecapError(data.error || 'Could not load recap.');
          setMeetingRecap({
            painPoints: [],
            summary: 'We could not generate a summary for this session.',
          });
        } else {
          setMeetingRecap({
            painPoints: Array.isArray(data.painPoints) ? data.painPoints : [],
            summary: typeof data.summary === 'string' ? data.summary : '',
          });
        }
      } catch (_) {
        if (!cancelled) {
          setRecapError('Network error while loading recap.');
          setMeetingRecap({
            painPoints: [],
            summary: 'Recap could not be loaded. You can still review the meeting in your notes.',
          });
        }
      } finally {
        if (!cancelled) setRecapLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [sessionOver, searchParams]);

  const demoTimerSyncedRef = useRef(false);
  const roomQueryParam = searchParams.get('room') || '';
  useEffect(() => {
    if (!demoSessionLive) {
      demoTimerSyncedRef.current = false;
      return;
    }
    if (demoTimerSyncedRef.current) return;
    const sid = roomQueryParam.replace(/^demo-/i, '').trim();
    if (!sid) return;
    demoTimerSyncedRef.current = true;
    fetch(`${API_URL}/api/session/demo-live`, {
      method : 'POST',
      headers: { 'Content-Type': 'application/json' },
      body   : JSON.stringify({ sessionId: sid }),
    }).catch(() => {});
  }, [demoSessionLive, roomQueryParam]);

  // If the room URL has no ?portalId= (shared link, new tab, cleared storage), ask the API
  // whether PORTAL_URL is set on the server and use the well-known env-portal UUID.
  useEffect(() => {
    if (portalId) {
      setPortalConfigReady(true);
      return undefined;
    }
    const ac = new AbortController();
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/config/public`, { signal: ac.signal });
        const j = await r.json();
        if (ac.signal.aborted || cancelled) return;
        if (j?.defaultPortalId) {
          setPortalId(j.defaultPortalId);
          try {
            const pr = await fetch(`${API_URL}/api/portals/${j.defaultPortalId}`, { signal: ac.signal });
            const pd = await pr.json();
            if (ac.signal.aborted || cancelled || !pd?.portal) return;
            if (pd.portal.url) setPortalUrl(pd.portal.url);
            if (pd.portal.name) setPortalName(pd.portal.name);
          } catch (_) { /* metadata optional */ }
        }
      } catch (_) { /* API unreachable — keep “no portal” UI */ }
      finally {
        if (!cancelled && !ac.signal.aborted) setPortalConfigReady(true);
      }
    })();
    return () => {
      cancelled = true;
      ac.abort();
    };
  }, [portalId]);

  useEffect(() => {
    if (!portalId || portalScreenshot) {
      setPortalStallHint('');
      return undefined;
    }
    const t = setTimeout(() => {
      const h = typeof window !== 'undefined' ? window.location.hostname : '';
      setPortalStallHint(
        `No dashboard image yet — check that the backend is running and reachable at ${API_URL}. ` +
          (h && h !== 'localhost' && h !== '127.0.0.1'
            ? `You opened this app via ${h}; use REACT_APP_API_URL=http://${h}:5000 or open http://localhost:3000. Backend must listen on 0.0.0.0:5000 for LAN.`
            : `Confirm REACT_APP_API_URL in frontend/.env and run: npx playwright install chromium in demoai/backend.`)
      );
    }, 14_000);
    return () => clearTimeout(t);
  }, [portalId, portalScreenshot]);

  // ── Socket: listen for session-ended and bot navigation actions ──────────

  useEffect(() => {
    // The URL ?room= param is the full LiveKit room name e.g. "demo-<uuid>".
    // The bot posts sessionId = just the UUID. Strip the prefix so IDs match.
    const roomName = searchParams.get('room') || '';
    const sessionId = roomName.replace(/^demo-/, '');
    const socket = io(API_URL);

    // Join the private socket room so events from other sessions are not received
    const joinRoom = () => {
      if (!sessionId) return;
      socket.emit('join-session', sessionId);
      // Playwright/bot often are not ready on first join — spaced forced captures beat “sometimes blank”.
      const forceShot = (ms) =>
        setTimeout(() => socket.emit('request-screenshot', { sessionId, force: true }), ms);
      forceShot(1800);
      forceShot(6000);
      forceShot(14000);
      forceShot(26000);
    };
    socket.on('connect', joinRoom);
    joinRoom(); // also try immediately if already connected

    socket.on('bot-status', (data) => {
      if (data.sessionId && sessionId && data.sessionId !== sessionId) return;
      if (data.step === 'session-ended' && data.status === 'completed') {
        endSession('Your demo session has ended. Thank you!', 'timer');
      }
      if (!demoSessionLiveRef.current) return;
      if (data.step === 'ai-thinking') setIsAiThinking(true);
      else if (data.step === 'ai-response') setIsAiThinking(false);
    });

    // User interrupted the bot mid-sentence → stop typewriter + reset all AI states
    socket.on('user-interrupted', (data) => {
      if (data?.sessionId && sessionId && data.sessionId !== sessionId) return;
      if (!demoSessionLiveRef.current) return;
      stopTypewriter(true);
      setIsAiThinking(false);
      setIsTyping(false);
      setTypingText('');
    });

    // Listen for user transcripts
    socket.on('user-transcript', (data) => {
      if (data?.sessionId && sessionId && data.sessionId !== sessionId) return;
      if (!demoSessionLiveRef.current) return;
      if (data.text) {
        stopTypewriter(true);
        setTranscript(prev => [...prev, { type: 'user', text: data.text, timestamp: new Date() }]);
        setIsAiThinking(true);
      }
    });

    // Live text chunk — update typing display while bot is speaking
    socket.on('ai-text-chunk', (data) => {
      if (data?.sessionId && sessionId && data.sessionId !== sessionId) return;
      if (!demoSessionLiveRef.current) return;
      if (data.text) {
        setIsAiThinking(false);
        setIsTyping(true);
        setTypingText(data.text);
        tryOpenCalendarFollowupSheet(data.text);
      }
    });

    // Full AI response — replace the live chunk
    socket.on('ai-response', (data) => {
      if (data?.sessionId && sessionId && data.sessionId !== sessionId) return;
      if (!demoSessionLiveRef.current) return;
      if (data.text) {
        tryOpenCalendarFollowupSheet(data.text);
        setIsAiThinking(false);
        setTypingText('');
        if (typingIntervalRef.current) {
          clearInterval(typingIntervalRef.current);
          typingIntervalRef.current = null;
        }
        setIsTyping(false);
        setTranscript(prev => [...prev, { type: 'ai', text: data.text, timestamp: new Date() }]);
      }
    });

    // ── Portal screenshot — update the left panel ─────────────────────────
    socket.on('portal-screenshot', (data) => {
      if (!sameDemoSessionId(sessionId, data.sessionId)) return;
      const b64 = normalizePortalScreenshotB64(data.screenshot);
      if (!b64 || b64.length < 80) return;
      // Skip sign-in step frames only (not substring "login" — that hides "Post-login…", etc.)
      if (isLoginPhasePortalLabel(data.label)) {
        setPortalLabel('Opening portal…');
        return;
      }
      screenshotReceivedRef.current = true;
      setPortalScreenshot(b64);
      setPortalLabel(data.label || 'Ready');
      window.dispatchEvent(new Event('portal-page-loaded'));
    });

    // ── Bot cursor target — animate cursor to the menu item before click ──
    socket.on('portal-cursor-target', (data) => {
      if (data.sessionId && sessionId && data.sessionId !== sessionId) return;
      const { relX, relY, label, section, viewportW, viewportH } = data;
      const panel = document.querySelector('.lk-portal-screenshot-panel');
      if (!panel) return;
      const fire = () => {
        const pt = mapPortalRelToScreenPoint(panel, relX, relY, viewportW, viewportH);
        window.dispatchEvent(
          new CustomEvent('bot-navigate-overlay', {
            detail: {
              section: label || section,
              targetRect: { x: pt.x, y: pt.y, width: 0, height: 0 },
            },
          })
        );
      };
      requestAnimationFrame(() => requestAnimationFrame(fire));
    });

    socket.on('bot-action', (data) => {
      if (typeof data.action !== 'string') return;
      if (data.sessionId && sessionId && data.sessionId !== sessionId) return;

      // portal-navigate: cursor is handled by portal-cursor-target event
      if (data.action === 'portal-navigate') return;

      // ── Scroll (portal mode) ─────────────────────────────────────────────
      if (data.action === 'scroll-down' || data.action === 'scroll-up') {
        const direction = data.action === 'scroll-down' ? 'down' : 'up';
        const amount    = data.amount || 'medium';
        fetch(`${API_URL}/api/portal/scroll`, {
          method : 'POST',
          headers: { 'Content-Type': 'application/json' },
          body   : JSON.stringify({ sessionId, direction, amount }),
        }).catch(() => {});
        setTranscript(prev => [...prev, { type: 'nav', text: `Scrolling ${direction}…`, timestamp: new Date() }]);
        return;
      }
    });

    return () => socket.disconnect();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── HTTP polling fallback for portal screenshot ───────────────────────────
  // If the Socket.IO 'portal-screenshot' event was missed on join, poll every 2 s.
  useEffect(() => {
    if (!portalId) return;
    const roomName = searchParams.get('room') || '';
    const sid      = roomName.replace(/^demo-/, '');
    if (!sid) return;

    let cancelled = false;
    (async () => {
      try {
        const r1 = await fetch(`${API_URL}/api/portal/ensure`, {
          method : 'POST',
          headers: { 'Content-Type': 'application/json' },
          body   : JSON.stringify({ sessionId: sid, portalId }),
        });
        const j1 = await r1.json().catch(() => ({}));
        if (!cancelled && !r1.ok && j1?.error) {
          setPortalLabel(`Error: ${j1.error}`);
        }
        if (!cancelled && r1.ok) {
          const b1 = normalizePortalScreenshotB64(j1.screenshot);
          if (b1.length >= 80 && !isLoginPhasePortalLabel(j1.label)) {
            screenshotReceivedRef.current = true;
            setPortalScreenshot(b1);
            setPortalLabel(j1.label || 'Ready');
          }
        }
        if (cancelled) return;
        const r2 = await fetch(`${API_URL}/api/portal/refresh-screenshot`, {
          method : 'POST',
          headers: { 'Content-Type': 'application/json' },
          body   : JSON.stringify({ sessionId: sid }),
        });
        const j2 = await r2.json().catch(() => ({}));
        if (cancelled) return;
        const b2 = normalizePortalScreenshotB64(j2.screenshot);
        if (b2.length >= 80 && !isLoginPhasePortalLabel(j2.label)) {
          screenshotReceivedRef.current = true;
          setPortalScreenshot(b2);
          setPortalLabel(j2.label || 'Ready');
        }
      } catch (_) { /* socket + poll will retry */ }
    })();

    let attempts = 0;
    const MAX_ATTEMPTS = 120; // 1 s interval → up to ~2 min while Playwright logs in
    const poll = setInterval(async () => {
      if (screenshotReceivedRef.current) { clearInterval(poll); return; }
      attempts++;
      if (attempts >= MAX_ATTEMPTS) {
        clearInterval(poll);
        setPortalLabel(
          'Error: Portal timeout — API host: npx playwright install chromium; set PORTAL_USERNAME/PASSWORD if login required; ' +
            'quote DB_PASSWORD in .env if it contains #; optional PLAYWRIGHT_DISABLE_SANDBOX=1 on Linux'
        );
        return;
      }
      try {
        const res  = await fetch(`${API_URL}/api/portal/snapshot/${sid}`);
        const data = await res.json();
        const b64 = normalizePortalScreenshotB64(data.screenshot);
        if (b64.length >= 80) {
          if (isLoginPhasePortalLabel(data.label)) {
            setPortalLabel('Opening portal…');
            return;
          }
          screenshotReceivedRef.current = true;
          setPortalScreenshot(b64);
          setPortalLabel(data.label || 'Ready');
          clearInterval(poll);
        }
      } catch (_) { /* ignore */ }
    }, 1000);
    return () => {
      cancelled = true;
      clearInterval(poll);
    };
  }, [portalId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Before LiveKit: one priming pass — refresh + reject blank-white frames ──
  useEffect(() => {
    if (!portalId || !String(portalId).trim()) return undefined;
    if (!portalFrameCandidateReady) return undefined;
    if (portalPrimedForLivekit) return undefined;

    const sid = (roomRouteKey || '').replace(/^demo-/i, '').trim();
    if (!sid) {
      setPortalPrimedForLivekit(true);
      return undefined;
    }

    const primeSessionId = ++portalPrimeSessionRef.current;
    let cancelled = false;
    const skipBlankCheck = (process.env.REACT_APP_SKIP_PORTAL_BLANK_CHECK || '').trim() === '1';
    const stillActive = () =>
      !cancelled && portalPrimeSessionRef.current === primeSessionId;

    void (async () => {
      for (let attempt = 0; attempt < 6; attempt++) {
        if (!stillActive()) return;
        try {
          const r = await fetch(`${API_URL}/api/portal/refresh-screenshot`, {
            method : 'POST',
            headers: { 'Content-Type': 'application/json' },
            body   : JSON.stringify({ sessionId: sid }),
          });
          const j = await r.json().catch(() => ({}));
          const b64 = normalizePortalScreenshotB64(j?.screenshot);
          if (b64.length < 80 || isLoginPhasePortalLabel(j?.label)) {
            await new Promise((res) => setTimeout(res, 500));
            continue;
          }
          if (stillActive()) {
            setPortalScreenshot(b64);
            setPortalLabel(j.label || 'Ready');
            screenshotReceivedRef.current = true;
          }
          if (skipBlankCheck) {
            if (stillActive()) setPortalPrimedForLivekit(true);
            return;
          }
          const blank = await isLikelyBlankWhitePngBase64(b64);
          if (!stillActive()) return;
          if (!blank) {
            setPortalPrimedForLivekit(true);
            return;
          }
          await new Promise((res) => setTimeout(res, 700));
        } catch (_) {
          await new Promise((res) => setTimeout(res, 400));
        }
      }
      if (stillActive()) setPortalPrimedForLivekit(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [portalFrameCandidateReady, portalId, roomRouteKey, portalPrimedForLivekit]);

  // ── Auto-scroll transcript to bottom ──────────────────────────────────────

  useEffect(() => {
    transcriptEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [transcript, isAiThinking, typingText]);

  // ── Typewriter effect for AI responses ────────────────────────────────────

  // Immediately cut the typewriter mid-sentence (user interrupted the bot)
  const stopTypewriter = useCallback((savePartial = true) => {
    if (typingIntervalRef.current) {
      clearInterval(typingIntervalRef.current);
      typingIntervalRef.current = null;
    }
    if (savePartial) {
      // Save whatever was typed so far with a visual cut marker
      setTypingText(prev => {
        if (prev) {
          setTranscript(t => [...t, {
            type: 'ai',
            text: prev,
            interrupted: true,   // flag for CSS styling
            timestamp: new Date(),
          }]);
        }
        return '';
      });
    } else {
      setTypingText('');
    }
    setIsTyping(false);
    setIsAiThinking(false);
  }, []);



  // Cleanup typewriter on unmount
  useEffect(() => {
    return () => {
      if (typingIntervalRef.current) {
        clearInterval(typingIntervalRef.current);
      }
    };
  }, []);

  // ── Connect to audio room ─────────────────────────────────────────────────

  const connect = useCallback(async () => {
    if (!livekitUrl || !token) {
      setError('Missing connection details. Please start a new session.');
      return;
    }

    setConnectionState('connecting');
    const turnUrl      = process.env.REACT_APP_TURN_URL      || '';
    const turnUsername = process.env.REACT_APP_TURN_USERNAME  || '';
    const turnPassword = process.env.REACT_APP_TURN_PASSWORD  || '';

    const iceServers = [
      { urls: 'stun:stun.l.google.com:19302' },
      { urls: 'stun:stun1.l.google.com:19302' },
    ];
    if (turnUrl && turnUsername && turnPassword) {
      iceServers.push({ urls: turnUrl, username: turnUsername, credential: turnPassword });
    }

    const room = new Room({
      adaptiveStream: true,
      dynacast: true,
      rtcConfig: { iceServers },
    });
    roomRef.current = room;

    room.on(RoomEvent.ConnectionStateChanged, state => {
      setConnectionState(state);
    });

    const updateParticipants = () => {
      setParticipants(
        [...room.remoteParticipants.values()].map(p => ({
          identity  : p.identity,
          isSpeaking: p.isSpeaking,
        }))
      );
    };

    room.on(RoomEvent.ParticipantConnected,    updateParticipants);
    room.on(RoomEvent.ActiveSpeakersChanged, speakers => {
      setIsBotSpeaking(speakers.some(s => s.identity === 'ai-demo-bot'));
      updateParticipants();
    });

    // When the bot leaves the room, the session is over
    room.on(RoomEvent.ParticipantDisconnected, participant => {
      updateParticipants();
      if (participant.identity === 'ai-demo-bot') {
        endSession('The AI bot has left — session complete.', 'call-ended');
      }
    });

    // Play remote audio tracks (bot voice)
    room.on(RoomEvent.TrackSubscribed, (track) => {
      if (track.kind === Track.Kind.Audio) {
        const audioEl = botAudioRef.current || document.createElement('audio');
        audioEl.autoplay = true;
        track.attach(audioEl);
        // Unlock audio after first user interaction (browser autoplay policy)
        room.startAudio().catch(() => {});
        if (!botAudioRef.current) {
          document.body.appendChild(audioEl);
          botAudioRef.current = audioEl;
        }
      }
    });

    room.on(RoomEvent.TrackUnsubscribed, track => track.detach());

    try {
      await room.connect(livekitUrl, token);
      setConnectionState('connected');

      // Unlock audio context on first user gesture (browser autoplay policy)
      const unlockAudio = () => { room.startAudio().catch(() => {}); document.removeEventListener('click', unlockAudio); };
      document.addEventListener('click', unlockAudio, { once: true });
      const localAudio = await createLocalAudioTrack({
        echoCancellation: true,   // prevent mic feedback from speakers
        noiseSuppression: false,  // let OpenAI VAD decide — browser suppression is too aggressive
        autoGainControl: false,   // don't boost mic artificially; keep natural volume
      }); 
      await room.localParticipant.publishTrack(localAudio);
      localTrackRef.current = localAudio;
      updateParticipants();
    } catch (err) {
      // React 18 Strict Mode runs mount → unmount → mount; cleanup calls disconnect() while
      // connect() is still running → LiveKit throws ConnectionError "Client initiated disconnect".
      const isCancelled =
        err instanceof ConnectionError && err.reason === ConnectionErrorReason.Cancelled;
      const msg = typeof err?.message === 'string' ? err.message : '';
      const looksLikeTeardown =
        isCancelled || /client initiated disconnect/i.test(msg);
      if (looksLikeTeardown || !roomSessionActiveRef.current) {
        return;
      }
      console.error('Room connection error:', err);
      const isFetchError = msg.includes('fetch') || msg.includes('network');
      const hint = isFetchError
        ? `Cannot reach the audio server. The server may be down or not publicly accessible.`
        : msg || 'Connection failed';
      setError(hint);
      setConnectionState('error');
      // Stop the countdown — the user is stuck and shouldn't be timed out
      if (countdownRef.current) clearInterval(countdownRef.current);
    }
  }, [livekitUrl, token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    roomSessionActiveRef.current = true;
    if (!livekitConnectReady) {
      setConnectionState('waiting-dashboard');
      return () => {
        roomSessionActiveRef.current = false;
        const r = roomRef.current;
        if (r) {
          r.disconnect().catch(() => {});
          roomRef.current = null;
        }
        if (botAudioRef.current) {
          botAudioRef.current.pause();
          botAudioRef.current.remove();
          botAudioRef.current = null;
        }
      };
    }
    connect();
    return () => {
      roomSessionActiveRef.current = false;
      const r = roomRef.current;
      if (r) {
        r.disconnect().catch(() => {});
        roomRef.current = null;
      }
      if (botAudioRef.current) {
        botAudioRef.current.pause();
        botAudioRef.current.remove();
        botAudioRef.current = null;
      }
    };
  }, [connect, livekitConnectReady]);

  // ── Mic toggle ────────────────────────────────────────────────────────────

  const toggleMute = async () => {
    const track = localTrackRef.current;
    if (!track) return;
    isMuted ? await track.unmute() : await track.mute();
    setIsMuted(prev => !prev);
  };

  // ── Shared helper: immediately tell backend the user has left ────────────
  const notifyUserLeft = useCallback((reason = 'user-left') => {
    const roomId  = searchParams.get('room') || '';
    const payload = JSON.stringify({ reason, sessionId: roomId });
    // sendBeacon works even during page unload; fetch is used when page stays open
    if (navigator.sendBeacon) {
      navigator.sendBeacon(`${API_URL}/api/session/user-left`, payload);
    } else {
      fetch(`${API_URL}/api/session/user-left`, {
        method   : 'POST',
        headers  : { 'Content-Type': 'application/json' },
        body     : payload,
        keepalive: true,
      }).catch(() => {});
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Leave room (manual — Leave button) — same recap screen as other endings ─
  const leaveRoom = async () => {
    notifyUserLeft('leave-button');
    endSession('You left the meeting.', 'call-ended');
  };

  // ── Notify backend on browser close / tab close / navigate away ──────────
  useEffect(() => {
    // beforeunload: fires on every page close, refresh, or navigation away
    const handleUnload = () => notifyUserLeft('page-unload');
    window.addEventListener('beforeunload', handleUnload);

    // visibilitychange: catches tab switching and minimising on mobile
    // If the tab stays hidden for >10 s, treat it as "user left"
    let hiddenTimer = null;
    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        hiddenTimer = setTimeout(() => notifyUserLeft('tab-hidden'), 10_000);
      } else {
        if (hiddenTimer) { clearTimeout(hiddenTimer); hiddenTimer = null; }
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      window.removeEventListener('beforeunload', handleUnload);
      document.removeEventListener('visibilitychange', handleVisibility);
      if (hiddenTimer) clearTimeout(hiddenTimer);
    };
  }, [notifyUserLeft]);

  // ── Helpers ───────────────────────────────────────────────────────────────

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = String(s % 60).padStart(2, '0');
    return `${m}:${sec}`;
  };

  const timerUrgent = timeLeft <= 60; // last minute → show in red

  // ── Session-ended overlay ─────────────────────────────────────────────────

  if (sessionOver) {
    const endBadge =
      sessionEndKind === 'timer'
        ? 'Session time ended'
        : sessionEndKind === 'call-ended'
        ? 'Call ended'
        : 'Meeting ended';
    return (
      <div className="lk-session-ended-overlay">
        <div className="lk-session-ended-card lk-session-ended-card--recap">
          <div className="lk-session-ended-icon">🎉</div>
          <h2>Session complete</h2>
          <p className="lk-session-ended-kind">{endBadge}</p>
          <p className="lk-session-ended-reason">{endReason}</p>

          <div className="lk-session-recap">
            <section className="lk-session-recap__section" aria-labelledby="lk-recap-pain">
              <h3 id="lk-recap-pain">Pain points</h3>
              {recapLoading ? (
                <p className="lk-session-recap__muted">Generating from your conversation…</p>
              ) : meetingRecap && meetingRecap.painPoints && meetingRecap.painPoints.length > 0 ? (
                <ul className="lk-session-recap__list">
                  {meetingRecap.painPoints.map((line, i) => (
                    <li key={i}>{line}</li>
                  ))}
                </ul>
              ) : (
                <p className="lk-session-recap__muted">
                  {meetingRecap && meetingRecap.summary && !meetingRecap.painPoints?.length
                    ? 'No clear pain points were called out — see the summary below.'
                    : 'None captured for this short session.'}
                </p>
              )}
            </section>
            <section className="lk-session-recap__section" aria-labelledby="lk-recap-summary">
              <h3 id="lk-recap-summary">Summary</h3>
              {recapLoading ? (
                <p className="lk-session-recap__muted">Generating from your conversation…</p>
              ) : (
                <p className="lk-session-recap__summary">
                  {(meetingRecap && meetingRecap.summary) ||
                    'Summary will appear here when ready.'}
                </p>
              )}
            </section>
            {recapError ? <p className="lk-session-recap__warn">{recapError}</p> : null}
          </div>

          <p className="lk-redirect-note">When you are finished reading, continue home.</p>
          <button type="button" className="lk-btn lk-btn-leave" onClick={() => navigate('/', { replace: true })}>
            Go home
          </button>
        </div>
      </div>
    );
  }

  // ── Main room UI ──────────────────────────────────────────────────────────

  return (
    <div
      className={`lk-room${fullWidthDashboard ? ' lk-room--full-dashboard' : ''}${
        screenshotCoverWidth ? ' lk-room--screenshot-cover' : ''
      }`}
    >
      {/* ── Left panel: portal screenshot ────────────────────────────────── */}
      <div className={`lk-dashboard-panel${fullWidthDashboard ? ' lk-dashboard-panel--full' : ''}`}>
        <div className={`lk-dashboard-header${fullWidthDashboard ? ' lk-dashboard-header--stretch' : ''}`}>
          <div className="lk-dashboard-header-left">
            <span className="lk-dashboard-label">
              {portalId ? portalName : 'Product Demo'}
            </span>
            {demoSessionLive && isBotSpeaking && (
              <span className="lk-bot-speaking-badge">
                <span className="lk-dot" /> AI is speaking…
              </span>
            )}
            {portalId && portalUrl && (
              <a href={portalUrl} target="_blank" rel="noreferrer"
                 className="lk-portal-open-link" title="Open portal in new tab">↗</a>
            )}
          </div>
          {fullWidthDashboard && (
            <div className="lk-meeting-bar-compact">
              <span
                className={`lk-bot-status lk-bot-status--compact ${
                  isBotSpeaking ? 'speaking' : demoSessionLive ? 'ready' : livekitConnectReady ? 'connecting' : 'waiting'
                }`}
              >
                {isPrimingDashboardForAudio
                  ? '● Sharpening…'
                  : !livekitConnectReady
                  ? '● Loading…'
                  : isBotSpeaking
                    ? '● Speaking…'
                    : demoSessionLive
                      ? '● Ready'
                      : connectionState === 'connecting'
                        ? '● Audio…'
                        : '● Connecting…'}
              </span>
              <span
                className={`lk-conn-dot lk-conn-${connectionState}`}
                title={String(connectionState)}
                aria-label={`Connection ${String(connectionState)}`}
              />
              <div
                className={`lk-countdown lk-countdown--compact ${timerUrgent ? 'urgent' : ''} ${
                  !demoSessionLive ? 'lk-countdown--hold' : ''
                }`}
              >
                <span className="lk-countdown-icon">{timerUrgent ? '⚠️' : '⏱️'}</span>
                <span className="lk-countdown-time">
                  {demoSessionLive ? formatTime(timeLeft) : formatTime(sessionLimitSec)}
                </span>
              </div>
              {demoSessionLive && isBotSpeaking && (
                <div className="lk-speaking-animation lk-speaking-animation--compact">
                  <div className="lk-bar" /><div className="lk-bar" /><div className="lk-bar" />
                  <div className="lk-bar" /><div className="lk-bar" />
                </div>
              )}
              <button
                type="button"
                className={`lk-btn lk-btn-mic lk-btn--compact ${isMuted ? 'muted' : ''}`}
                onClick={toggleMute}
                disabled={!demoSessionLive}
              >
                {isMuted ? '🔇' : '🎤'}
              </button>
              <button type="button" className="lk-btn lk-btn-leave lk-btn--compact" onClick={leaveRoom}>
                Leave
              </button>
            </div>
          )}
        </div>

        {fullWidthDashboard && !demoSessionLive && livekitConnectReady && (
          <p className="lk-demo-hold-banner">Timer and voice start after you connect to audio.</p>
        )}
        {fullWidthDashboard && !livekitConnectReady && portalId && (
          <p className="lk-demo-hold-banner">Timer and voice start when the dashboard preview is ready.</p>
        )}
        {fullWidthDashboard && error && (
          <div className="lk-error lk-error--banner">
            <p>{error}</p>
            <button type="button" className="lk-btn-retry" onClick={connect}>↺ Retry</button>
          </div>
        )}

        <div className="lk-iframe-wrapper">
          {portalId ? (
            <div className="lk-portal-screenshot-panel">
              {/* Spinner until first screenshot arrives */}
              {!portalScreenshot && (
                <div className="lk-iframe-loading">
                  <div className="lk-iframe-loading-inner">
                    <div className="lk-iframe-spinner" />
                    <span className="lk-iframe-loading-label">{portalLabel}</span>
                    <span className="lk-iframe-loading-sub">{portalName} — logging in, please wait…</span>
                    {portalStallHint && (
                      <p className="lk-portal-stall-hint" role="status">{portalStallHint}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Live screenshot */}
              {portalScreenshot && (
                <div className="lk-portal-screenshot-wrap">
                  <img
                    src={`data:image/png;base64,${portalScreenshot}`}
                    alt="Portal"
                    className="lk-portal-screenshot-img"
                    style={portalLabel?.startsWith('Error:') ? { filter: 'brightness(0.4)' } : {}}
                  />
                  {isPrimingDashboardForAudio && (
                    <div className="lk-portal-priming-overlay" role="status">
                      <div className="lk-portal-priming-inner">
                        <div className="lk-iframe-spinner" />
                        <span>Preparing dashboard for audio…</span>
                      </div>
                    </div>
                  )}
                  {portalLabel?.startsWith('Error:') && (
                    <div className="lk-portal-error-overlay">
                      <div className="lk-portal-error-icon">⚠️</div>
                      <div className="lk-portal-error-title">Portal could not be loaded</div>
                      <div className="lk-portal-error-msg">{portalLabel.replace(/^Error:\s*/i, '')}</div>
                    </div>
                  )}
                  {portalLabel && portalLabel !== 'Ready' && !portalLabel.startsWith('Error:') && (
                    <div className="lk-portal-step-label">
                      <span className="lk-portal-step-dot" /> {portalLabel}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="lk-iframe-loading">
              <div className="lk-iframe-loading-inner">
                <div className="lk-iframe-spinner" />
                <span className="lk-iframe-loading-label">No portal configured</span>
                <span className="lk-iframe-loading-sub">Add PORTAL_URL credentials in backend/.env</span>
              </div>
            </div>
          )}

        </div>
      </div>

      {/* ── Right panel: conversation + controls (hidden in full-width dashboard mode) ── */}
      {!fullWidthDashboard && (
      <div className="lk-controls-panel">

        {/* ── Top bar: bot status + countdown ── */}
        <div className="lk-topbar">
          <div className="lk-topbar-bot">
            <span className="lk-topbar-icon">🤖</span>
            <div className="lk-topbar-info">
              <span className="lk-topbar-name">{BOT_UI_NAME}</span>
              <span
                className={`lk-bot-status ${
                  isBotSpeaking ? 'speaking' : demoSessionLive ? 'ready' : livekitConnectReady ? 'connecting' : 'waiting'
                }`}
              >
                {isPrimingDashboardForAudio
                  ? '● Sharpening dashboard preview…'
                  : !livekitConnectReady
                  ? '● Dashboard loading…'
                  : isBotSpeaking
                    ? '● Speaking…'
                    : demoSessionLive
                      ? '● Ready'
                      : connectionState === 'connecting'
                        ? '● Connecting audio…'
                        : '● Connecting…'}
              </span>
            </div>
          </div>
          <div className={`lk-countdown ${timerUrgent ? 'urgent' : ''} ${!demoSessionLive ? 'lk-countdown--hold' : ''}`}>
            <span className="lk-countdown-icon">{timerUrgent ? '⚠️' : '⏱️'}</span>
            <span className="lk-countdown-time">
              {demoSessionLive ? formatTime(timeLeft) : formatTime(sessionLimitSec)}
            </span>
          </div>
        </div>

        {!demoSessionLive && livekitConnectReady && (
          <p className="lk-demo-hold-hint">Timer and voice chat start after you connect to audio.</p>
        )}
        {!livekitConnectReady && portalId && (
          <p className="lk-demo-hold-hint">Timer and voice chat start when the dashboard preview appears on the left.</p>
        )}

        {/* Speaking animation — compact inline bar */}
        {demoSessionLive && isBotSpeaking && (
          <div className="lk-speaking-animation">
            <div className="lk-bar" /><div className="lk-bar" /><div className="lk-bar" />
            <div className="lk-bar" /><div className="lk-bar" />
          </div>
        )}

        {error && (
          <div className="lk-error">
            <p>{error}</p>
            <button className="lk-btn-retry" onClick={connect}>↺ Retry</button>
          </div>
        )}

        {/* ── Chat transcript — main focal area ── */}
        <div className="lk-transcript">
          <div className="lk-transcript-header">
            <span>💬 Conversation</span>
            <span className={`lk-conn-dot lk-conn-${connectionState}`} title={connectionState} />
          </div>
          <div className="lk-transcript-messages">

            {!demoSessionLive && (
              <div className="lk-transcript-placeholder">
                <p>
                  The conversation will appear here once you are connected and the session timer has started.
                </p>
              </div>
            )}

            {/* Settled messages */}
            {demoSessionLive && transcript.map((msg, i) => {
              if (msg.type === 'nav') {
                return (
                  <div key={i} className="lk-nav-event">
                    <span>📂</span> {msg.text}
                  </div>
                );
              }
              return (
                <div
                  key={i}
                  className={`lk-transcript-message lk-message-${msg.type}${msg.interrupted ? ' lk-message-interrupted' : ''}`}
                >
                  <div className="lk-message-icon">
                    {msg.type === 'user' ? '👤' : '🤖'}
                  </div>
                  <div className="lk-message-content">
                    <div className="lk-message-label">
                      {msg.type === 'user' ? 'You' : 'AI Assistant'}
                      {msg.interrupted && <span className="lk-interrupted-badge">interrupted</span>}
                    </div>
                    <div className="lk-message-text">
                      {msg.text}
                      {msg.interrupted && <span className="lk-cut-mark"> ▌</span>}
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Thinking dots */}
            {demoSessionLive && isAiThinking && !isTyping && (
              <div className="lk-transcript-message lk-message-ai">
                <div className="lk-message-icon">🤖</div>
                <div className="lk-message-content">
                  <div className="lk-message-label">AI Assistant</div>
                  <div className="lk-thinking-dots">
                    <span className="lk-dot" /><span className="lk-dot" /><span className="lk-dot" />
                  </div>
                </div>
              </div>
            )}

            {/* Typewriter — live AI response streaming word by word */}
            {demoSessionLive && isTyping && typingText && (
              <div className="lk-transcript-message lk-message-ai lk-typing">
                <div className="lk-message-icon">🤖</div>
                <div className="lk-message-content">
                  <div className="lk-message-label">AI Assistant</div>
                  <div className="lk-message-text">
                    {typingText}<span className="lk-typing-cursor">|</span>
                  </div>
                </div>
              </div>
            )}

            <div ref={transcriptEndRef} />
          </div>
        </div>

        {/* ── Controls ── */}
        <div className="lk-button-group">
          <button
            className={`lk-btn lk-btn-mic ${isMuted ? 'muted' : ''}`}
            onClick={toggleMute}
            disabled={!demoSessionLive}
          >
            {isMuted ? '🔇 Unmute' : '🎤 Mute'}
          </button>
          <button className="lk-btn lk-btn-leave" onClick={leaveRoom}>
            🚪 Leave
          </button>
        </div>
      </div>
      )}

      {/* Calendar follow-up — when bot delivers Phase 7 pricing pivot (socket transcript) */}
      {calendarSheetOpen && (
        <div className="lk-calendar-sheet" role="dialog" aria-modal="true" aria-labelledby="lk-calendar-sheet-title">
          <button
            type="button"
            className="lk-calendar-sheet__backdrop"
            aria-label="Dismiss"
            onClick={() => setCalendarSheetOpen(false)}
          />
          <div className="lk-calendar-sheet__panel">
            <div className="lk-calendar-sheet__header">
              <h2 id="lk-calendar-sheet-title" className="lk-calendar-sheet__title">
                Book a follow-up
              </h2>
              <button
                type="button"
                className="lk-calendar-sheet__close"
                onClick={() => setCalendarSheetOpen(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <p className="lk-calendar-sheet__body">
              {BOT_UI_NAME} offered a follow-up to go deeper on pricing and how the product would fit your workflow.
              {HILDA_CALENDAR_URL
                ? ' Pick a time below when it works for you.'
                : ' Your host can send a scheduling link—ask them if you need it sooner.'}{' '}
              For pricing or commercial questions anytime, use the email address below.
            </p>
            <p className="lk-calendar-sheet__email-line">
              <a
                className="lk-calendar-sheet__mailto"
                href={`mailto:${HILDA_PRICING_EMAIL}?subject=${encodeURIComponent('Pricing question — demo follow-up')}`}
              >
                {HILDA_PRICING_EMAIL}
              </a>
              <span className="lk-calendar-sheet__email-note"> — quotes, plans, or what you saw in this session.</span>
            </p>
            {HILDA_CALENDAR_URL ? (
              <a
                className="lk-calendar-sheet__cta"
                href={HILDA_CALENDAR_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open calendar
              </a>
            ) : (
              <p className="lk-calendar-sheet__hint">Your host will share a calendar link separately.</p>
            )}
          </div>
        </div>
      )}

      {/* Animated bot cursor — follows the portal cursor-target events */}
      <BotCursorOverlay />
    </div>
  );
}
